/**
 * Theme Constants
 *
 * Theme definitions, color palettes, and mappings.
 * Extracted from ThemeContext.js for reusability.
 *
 * @module store/theme/themeConstants
 */

// SHARED light/dark mode colors - define once, use everywhere
export const lightModeColors = {
  background: '#EEEEEE',
  surface: '#f3f0f0',
  textPrimary: '#1A1A1A',
  textSecondary: '#666666',
  textMuted: '#999999',
  borderColor: '#DDDDDD',
  cardBackground: '#FFFFFF',
  inputBackground: '#EEEEEE',
  shadowBase: 'rgba(0, 0, 0, 0.1)',
  filterBorderColor: '#CCCCCC',
  filterBorderHover: '#BBBBBB'
};

export const darkModeColors = {
  background: '#1E1E1E',
  surface: '#252526',
  textPrimary: '#FFFFFF',
  textSecondary: '#BFBFBF',
  textMuted: '#909090',
  borderColor: '#3A3A3A',
  cardBackground: '#1E1E1E',
  inputBackground: '#1E1E1E',
  shadowBase: 'rgba(0, 0, 0, 0.35)',
  filterBorderColor: '#4A4A4A',
  filterBorderHover: '#5A5A5A'
};

// Clean theme definitions
export const themes = {
  default: {
    name: 'Purple',
    primary: '#9404C3',
    secondary: '#530170',
    accent: '#700295',
    accentHighlight: '#710296',
    borderColors: {
      theme: 'rgba(148, 4, 195, 0.4)',
      themeHover: 'rgba(148, 4, 195, 0.6)'
    },
    shadowColors: {
      light: { r: 0.588235, g: 0.0117647, b: 0.784314 },
      mid: { r: 0.438431, g: 0.0078431, b: 0.580392 },
      dark: { r: 0.298039, g: 0.00392157, b: 0.396078 },
      accent: { r: 0.517647, g: 0.00784314, b: 0.690196 }
    },
    shadows: {
      light: 'rgba(148, 4, 195, 0.3)',
      dark: 'rgba(77, 5, 101, 0.9)',
      mid: 'rgba(112, 2, 149, 0.5)'
    },
    gradients: {
      primary: 'linear-gradient(to top, #4d0565, #9404C3)',
      secondary: 'linear-gradient(90deg, #9404C3 0%, #47025D 100%)'
    },
    indicators: {
      path: 'rgba(148, 4, 195, 0.05)',
      border: 'rgba(148, 4, 195, 0.5)'
    }
  },
  purple: {
    name: 'Purple',
    primary: '#9404C3',
    secondary: '#530170',
    accent: '#700295',
    accentHighlight: '#710296',
    borderColors: {
      theme: 'rgba(148, 4, 195, 0.4)',
      themeHover: 'rgba(148, 4, 195, 0.6)'
    },
    shadowColors: {
      light: { r: 0.588235, g: 0.0117647, b: 0.784314 },
      mid: { r: 0.438431, g: 0.0078431, b: 0.580392 },
      dark: { r: 0.298039, g: 0.00392157, b: 0.396078 },
      accent: { r: 0.517647, g: 0.00784314, b: 0.690196 }
    },
    shadows: {
      light: 'rgba(148, 4, 195, 0.3)',
      dark: 'rgba(77, 5, 101, 0.9)',
      mid: 'rgba(112, 2, 149, 0.5)'
    },
    gradients: {
      primary: 'linear-gradient(to top, #4d0565, #9404C3)',
      secondary: 'linear-gradient(90deg, #9404C3 0%, #47025D 100%)'
    },
    indicators: {
      path: 'rgba(148, 4, 195, 0.05)'
    }
  },
  green: {
    name: 'Green',
    primary: '#408122',
    secondary: '#0E4008',
    accent: '#2F5C17',
    accentHighlight: '#375328',
    borderColors: {
      theme: 'rgba(64, 129, 34, 0.4)',
      themeHover: 'rgba(64, 129, 34, 0.6)'
    },
    shadowColors: {
      light: { r: 0.321569, g: 0.647059, b: 0.176471 },
      mid: { r: 0.184314, g: 0.360784, b: 0.090196 },
      dark: { r: 0.054902, g: 0.250980, b: 0.031373 },
      accent: { r: 0.254902, g: 0.505882, b: 0.133333 }
    },
    shadows: {
      light: 'rgba(64, 129, 34, 0.3)',
      dark: 'rgba(47, 92, 23, 0.5)',
      mid: 'rgba(14, 64, 8, 0.9)'
    },
    gradients: {
      primary: 'linear-gradient(to top, #0E4008, #408122)',
      secondary: 'linear-gradient(90deg, #408122 0%, #0E4008 100%)'
    },
    indicators: {
      path: 'rgba(4, 195, 14, 0.05)'
    }
  },
blue: {
  name: 'Blue',
  primary: '#0033A1',
  secondary: '#00246B',
  accent: '#005EB8',
  accentHighlight: '#2F7DE1',

  borderColors: {
    theme: 'rgba(0, 51, 161, 0.35)',
    themeHover: 'rgba(0, 51, 161, 0.55)'
  },

  shadowColors: {
    light: { r: 0.0, g: 0.368627, b: 0.721569 },
    mid:   { r: 0.0, g: 0.2, b: 0.631373 },
    dark:  { r: 0.0, g: 0.141176, b: 0.419608 },
    accent:{ r: 0.184314, g: 0.490196, b: 0.882353 }
  },

  shadows: {
    light: 'rgba(0, 94, 184, 0.25)',
    mid: 'rgba(0, 51, 161, 0.6)',
    dark: 'rgba(0, 36, 107, 0.85)'
  },

  gradients: {
    primary: 'linear-gradient(to top, #00246B, #0033A1)',
    secondary: 'linear-gradient(90deg, #0033A1 0%, #00246B 100%)'
  },

  indicators: {
    path: 'rgba(0, 94, 184, 0.04)'
  }
},
  red: {
    name: 'Red',
    primary: '#E03C31',
    secondary: '#711913',
    accent: '#A2261D',
    accentHighlight: '#B5352B',
    borderColors: {
      theme: 'rgba(224, 60, 49, 0.4)',
      themeHover: 'rgba(224, 60, 49, 0.6)'
    },
    shadowColors: {
      light: { r: 0.933333, g: 0.266667, b: 0.192157 },
      mid: { r: 0.878431, g: 0.235294, b: 0.192157 },
      dark: { r: 0.443137, g: 0.098039, b: 0.074510 },
      accent: { r: 0.635294, g: 0.149020, b: 0.113725 }
    },
    shadows: {
      light: 'rgba(224, 60, 49, 0.3)',
      dark: 'rgba(162, 38, 29, 0.5)',
      mid: 'rgba(113, 25, 19, 0.9)'
    },
    gradients: {
      primary: 'linear-gradient(to top, #711913, #E03C31)',
      secondary: 'linear-gradient(90deg, #E03C31 0%, #711913 100%)'
    },
    indicators: {
      path: 'rgba(195, 42, 4, 0.05)'
    }
  },
  yellow: {
    name: 'Yellow',
    primary: '#d8b732',
    secondary: '#d09a11',
    accent: '#d2a228',
    accentHighlight: '#d4ab44',
    borderColors: {
      theme: 'rgba(216, 183, 50, 0.4)',
      themeHover: 'rgba(216, 183, 50, 0.6)'
    },
    shadowColors: {
      light: { r: 0.901961, g: 0.752941, b: 0.250980 },
      mid: { r: 0.800000, g: 0.674510, b: 0.180392 },
      dark: { r: 0.714118, g: 0.584314, b: 0.062745 },
      accent: { r: 0.627451, g: 0.450980, b: 0.000000 }
    },
    shadows: {
      light: 'rgba(204, 172, 46, 0.3)',
      dark: 'rgba(160, 115, 0, 0.5)',
      mid: 'rgba(139, 100, 0, 0.9)'
    },
    gradients: {
      primary: 'linear-gradient(to top, #d09a11, #d8b732)',
      secondary: 'linear-gradient(90deg, #d8b732 0%, #d09a11 100%)'
    },
    indicators: {
      path: 'rgba(195, 179, 4, 0.05)'
    }
  }
};

// Map hex codes to theme names for backward compatibility.
// Includes both primary hex values from theme definitions and common alternatives.
// Going forward, the API stores colour names ('yellow', 'blue') not hex codes.
export const hexToThemeMap = {
  '#9404C3': 'purple',
  '#530170': 'purple',
  '#4B89E7': 'blue',
  '#3B82F6': 'blue',
  '#113976': 'blue',
  '#408122': 'green',
  '#32a852': 'green',
  '#0E4008': 'green',
  '#E03C31': 'red',
  '#EF4444': 'red',
  '#711913': 'red',
  '#d8b732': 'yellow',
  '#d09a11': 'yellow',
  '#ccac2e': 'yellow',
  '#F59E0B': 'yellow'
};

/**
 * Generate a full theme palette from a single primary hex color.
 * Uses HSL color theory to derive secondary, accent, gradients, and shadows.
 * @param {string} primaryHex - e.g. "#FF6B00"
 * @returns {Object} Theme palette compatible with the `themes` structure
 */
export const generatePaletteFromHex = (primaryHex) => {
  const r = parseInt(primaryHex.slice(1, 3), 16);
  const g = parseInt(primaryHex.slice(3, 5), 16);
  const b = parseInt(primaryHex.slice(5, 7), 16);

  // RGB → HSL
  const rn = r / 255, gn = g / 255, bn = b / 255;
  const max = Math.max(rn, gn, bn), min = Math.min(rn, gn, bn);
  const d = max - min;
  let h = 0, s = 0, l = (max + min) / 2;
  if (d !== 0) {
    s = d / (1 - Math.abs(2 * l - 1));
    switch (max) {
      case rn: h = ((gn - bn) / d % 6 + 6) % 6; break;
      case gn: h = (bn - rn) / d + 2; break;
      default: h = (rn - gn) / d + 4;
    }
    h = h / 6;
  }
  h *= 360; s *= 100; l *= 100;

  const hslToHex = (hh, ss, ll) => {
    ss /= 100; ll /= 100;
    const a = ss * Math.min(ll, 1 - ll);
    const f = n => {
      const k = (n + hh / 30) % 12;
      const color = ll - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);
      return Math.round(255 * Math.max(0, Math.min(1, color))).toString(16).padStart(2, '0');
    };
    return `#${f(0)}${f(8)}${f(4)}`;
  };

  const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));

  const secondary = hslToHex(h, clamp(s + 5, 10, 100), clamp(l - 42, 5, 40));
  const accent = hslToHex(h, clamp(s + 3, 10, 100), clamp(l - 18, 5, 65));
  const accentHighlight = hslToHex(h, clamp(s + 5, 10, 100), clamp(l - 10, 5, 65));
  // Same-hue gradient stops — lighter and darker shades of the primary, never near-black
  const gradientLight = hslToHex(h, clamp(s - 5, 10, 100), clamp(l + 14, 10, 68));
  const gradientDark  = hslToHex(h, clamp(s + 8, 10, 100), clamp(l - 22, 18, 55));

  // Normalized 0-1 floats for SVG filter matrices (used by TopNavBtn, etc.)
  const rn255 = r / 255, gn255 = g / 255, bn255 = b / 255;

  return {
    name: 'Custom',
    primary: primaryHex,
    secondary,
    accent,
    accentHighlight,
    borderColors: {
      theme: `rgba(${r},${g},${b},0.4)`,
      themeHover: `rgba(${r},${g},${b},0.6)`
    },
    shadowColors: {
      light:  { r: rn255,            g: gn255,            b: bn255 },
      mid:    { r: rn255 * 0.75,     g: gn255 * 0.75,     b: bn255 * 0.75 },
      dark:   { r: rn255 * 0.50,     g: gn255 * 0.50,     b: bn255 * 0.50 },
      accent: { r: rn255 * 0.875,    g: gn255 * 0.875,    b: bn255 * 0.875 }
    },
    shadows: {
      light: `rgba(${r},${g},${b},0.3)`,
      dark: `rgba(${r},${g},${b},0.9)`,
      mid: `rgba(${r},${g},${b},0.5)`
    },
    gradients: {
      primary: `linear-gradient(to top, ${gradientDark}, ${gradientLight})`,
      secondary: `linear-gradient(90deg, ${gradientLight} 0%, ${gradientDark} 100%)`
    },
    indicators: {
      path: `rgba(${r},${g},${b},0.05)`
    },
    tints: {
      hover: `rgba(${r},${g},${b},0.07)`,
      drag: `rgba(${r},${g},${b},0.14)`
    }
  };
};

/**
 * Get theme name from hex code
 * @param {string} hexCode - Hex color code
 * @returns {string} Theme name
 */
export const getThemeNameFromHex = (hexCode) => {
  return hexToThemeMap[hexCode] || 'purple';
};

/**
 * Get hex from theme name
 * @param {string} themeName - Theme name
 * @returns {string} Primary hex color
 */
export const getHexFromThemeName = (themeName) => {
  return themes[themeName]?.primary || themes.default.primary;
};

/**
 * Extract theme from userData object
 * @param {Object} userData - User data object
 * @returns {string|null} Theme name or null
 */
export const extractThemeFromUserData = (userData) => {
  const userTheme = userData?.orgTheme ||
    userData?.currentOrganization?.orgTheme ||
    userData?.organizations?.[0]?.orgTheme ||
    userData?.theme ||
    userData?.currentOrganization?.theme ||
    userData?.organizations?.[0]?.theme;

  if (userTheme) {
    return userTheme.startsWith('#') ? getThemeNameFromHex(userTheme) : userTheme;
  }
  return null;
};
